export const seasonZeroPage = Object.freeze({
  path: "/season-0",
  navLabel: "Season 0",
  seo: {
    titleBase: "Season 0",
    description: "Learn the Season 0 market flow, see the wallets moving RaizIQ, and preview what is coming next."
  },
  hero: {
    eyebrow: "Season 0",
    title: "Season 0 is where the board starts moving.",
    description:
      "Create markets, vote with SOL, rally the winning side, and help shape Season 0.",
    primaryCta: { label: "Browse markets", to: "/markets" },
    secondaryCta: { label: "Create a market", to: "/create" }
  },
  process: {
    title: "How Season 0 markets work.",
    description:
      "The flow is simple on the surface: create a market, vote into the side you want to win, and settle the remaining escrow to the voters who backed the winning option.",
    steps: [
      {
        title: "Create a market",
        body:
          "A creator publishes the question, options, timing, and context. Once the market is live, it becomes a board that players can move."
      },
      {
        title: "Vote with SOL",
        body:
          "Players vote by sending SOL through the current vote flow. Finalized votes update the visible option totals before market close."
      },
      {
        title: "Most SOL wins",
        body:
          "At close, the winning option is the option with the most finalized escrowed SOL. The board is decided by the weight players put behind each option."
      },
      {
        title: "Winning voters share escrow",
        body:
          "Everyone who voted for the winning option shares the remaining winning escrow. Each wallet's share is based on its share of the SOL voted for that winning option."
      },
      {
        title: "Disbursement follows close",
        body:
          "During Season 0, winning disbursement is handled within 24 hours after market close until automated disbursement is turned on."
      }
    ]
  },
  participants: {
    title: "Season 0 participants",
    description:
      "This list includes every wallet that has created a market or cast a finalized vote. Linked X and SNS identities show as handles; the rest are shortened wallet addresses."
  },
  upcoming: {
    title: "Upcoming features",
    description: "Season 0 is the live arena. The next drops make markets easier to share, easier to read, and easier to play anywhere.",
    features: [
      {
        title: "Market Blinks",
        body:
          "Market actions as shareable Solana Blinks, so a market can travel through X, chats, wallets, and any client that can render an action.",
        accent: "sky",
        href: "https://solana.com/developers/guides/advanced/actions#blinks"
      },
      {
        title: "Sharper metrics",
        body:
          "Cleaner readouts for momentum, vote flow, market pressure, creator activity, and player movement across the board.",
        accent: "emerald"
      },
      {
        title: "More embedded wallet support",
        body:
          "More ways for new players to arrive, connect, and vote without leaving the flow that brought them into the market.",
        accent: "violet"
      },
      {
        title: "Solana Seeker app",
        body:
          "A mobile-native path for the Solana Seeker crowd, tuned for market discovery, wallet signing, and fast participation.",
        accent: "amber"
      },
      {
        title: "Player points",
        body:
          "Points tracking for players so Season 0 activity can turn into a visible record of who created, voted, rallied, and moved markets.",
        accent: "rose"
      }
    ]
  },
  finalCta: {
    title: "Help define the first season.",
    body: "Create a market, vote into a live board, and bring people into the side you think can win.",
    links: [
      { label: "Browse markets", to: "/markets", variant: "primary" },
      { label: "Create a market", to: "/create", variant: "secondary" }
    ]
  }
});

export function buildSeasonZeroParticipants({
  creatorWallets = [],
  voterWallets = [],
  walletXIdentities = new Map(),
  snsXIdentities = new Map()
} = {}) {
  const participantsByWallet = new Map();

  for (const walletAddress of creatorWallets) {
    upsertSeasonZeroParticipant(participantsByWallet, walletAddress, "Creator");
  }
  for (const walletAddress of voterWallets) {
    upsertSeasonZeroParticipant(participantsByWallet, walletAddress, "Voter");
  }

  return [...participantsByWallet.values()]
    .map((participant) => ({
      ...participant,
      ...seasonZeroIdentityFor(participant.walletAddress, { walletXIdentities, snsXIdentities })
    }))
    .sort(compareSeasonZeroParticipants);
}

function upsertSeasonZeroParticipant(participantsByWallet, walletAddress, role) {
  const wallet = String(walletAddress || "").trim();
  if (!wallet) return;
  const participant =
    participantsByWallet.get(wallet) ||
    {
      walletAddress: wallet,
      roles: []
    };
  if (!participant.roles.includes(role)) {
    participant.roles.push(role);
  }
  participantsByWallet.set(wallet, participant);
}

function seasonZeroIdentityFor(walletAddress, { walletXIdentities, snsXIdentities }) {
  const linkedIdentity = walletXIdentities.get(walletAddress);
  if (linkedIdentity?.linkStatus === "linked" && linkedIdentity?.xHandle) {
    return {
      displayName: `@${linkedIdentity.xHandle}`,
      displayKind: "linked_x",
      xHandle: linkedIdentity.xHandle,
      xUserId: linkedIdentity.xUserId || null
    };
  }

  const snsIdentity = snsXIdentities.get(walletAddress);
  if (snsIdentity?.lookupStatus === "found" && snsIdentity?.verifiedOwner && snsIdentity?.xHandle) {
    return {
      displayName: `@${snsIdentity.xHandle}`,
      displayKind: "sns_x",
      xHandle: snsIdentity.xHandle,
      xUserId: null
    };
  }

  return {
    displayName: null,
    displayKind: "wallet",
    xHandle: null,
    xUserId: null
  };
}

function compareSeasonZeroParticipants(left, right) {
  const leftLabel = seasonZeroSortLabel(left);
  const rightLabel = seasonZeroSortLabel(right);
  return (
    leftLabel.localeCompare(rightLabel, "en", { sensitivity: "base" }) ||
    left.walletAddress.localeCompare(right.walletAddress)
  );
}

function seasonZeroSortLabel(participant) {
  return String(participant.displayName || participant.walletAddress || "").replace(/^@/, "").toLowerCase();
}
